import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Pencil, Trash2 } from 'lucide-react';
import { deleteLegalCaseRecord } from '../../services/api/implementation/impl';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import LegalRecordModal from './LegalRecordModal';
import { canDeleteLegalRecord, canWriteLegalRecords, localToday } from './legalCase';
import { LEGAL_RECORDS } from './legalRecords';

function cellContent(column, row, ctx) {
    if (column.type === 'links') {
        const links = [
            row.fileUrl && { href: row.fileUrl, label: 'File' },
            row.link && { href: row.link, label: 'Link' }
        ].filter(Boolean);
        return links.length ? (
            <span className="flex gap-3">
                {links.map((link) => (
                    <a
                        key={link.label}
                        href={link.href}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[#346C75] underline">
                        {link.label}
                    </a>
                ))}
            </span>
        ) : (
            '---'
        );
    }
    const value = column.render(row, ctx);
    return value === null || value === undefined || value === '' ? '---' : value;
}

/** One tab of a court case: its hearings, follow-ups or documents. */
const LegalRecordsPanel = ({ task, kind }) => {
    const config = LEGAL_RECORDS[kind];
    const queryClient = useQueryClient();
    // { row } while the add/edit modal is open; row is null for a new record.
    const [editing, setEditing] = useState(null);
    const [deleting, setDeleting] = useState(null);

    const deleteMutation = useMutation({
        mutationFn: (row) => deleteLegalCaseRecord(task.id, config.path, row.id),
        onSuccess: () => {
            toast.success(`${config.noun} deleted`);
            setDeleting(null);
            queryClient.invalidateQueries(['managementTask']);
        },
        onError: (error) => {
            toast.error(error?.response?.data?.message || 'Failed to delete');
        }
    });

    const rows = [...(task?.legalCase?.[config.listKey] || [])].sort(config.sort);
    const canWrite = canWriteLegalRecords(task);
    const ctx = { today: localToday() };

    return (
        <>
            {editing && (
                <LegalRecordModal
                    task={task}
                    kind={kind}
                    row={editing.row}
                    onClose={() => setEditing(null)}
                />
            )}
            {deleting && (
                <ConfirmDeleteModal
                    title={`Delete ${config.noun.toLowerCase()}`}
                    name={config.nameOf(deleting)}
                    detail="The removal is written to the case history."
                    busy={deleteMutation.isPending}
                    onConfirm={() => deleteMutation.mutate(deleting)}
                    onClose={() => setDeleting(null)}
                />
            )}
            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-800">{config.title}</h2>
                        <p className="mt-1 text-sm text-gray-500">{config.hint}</p>
                    </div>
                    {canWrite && (
                        <button
                            type="button"
                            className="bg-[#346C75] text-white px-4 py-2 rounded hover:bg-[#2a565e]"
                            onClick={() => setEditing({ row: null })}
                        >
                            + Add {config.noun.toLowerCase()}
                        </button>
                    )}
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full text-left">
                        <thead className="bg-gray-50 text-gray-600">
                            <tr>
                                {config.columns.map((column) => (
                                    <th key={column.label} className="px-5 py-3 font-medium text-sm">
                                        {column.label}
                                    </th>
                                ))}
                                {canWrite && <th className="px-5 py-3 font-medium text-sm">Action</th>}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {rows.length > 0 ? (
                                rows.map((row) => (
                                    <tr key={row.id} className="hover:bg-gray-50 align-top">
                                        {config.columns.map((column) => (
                                            <td
                                                key={column.label}
                                                className="px-5 py-3 text-sm text-gray-700 whitespace-pre-wrap">
                                                {cellContent(column, row, ctx)}
                                            </td>
                                        ))}
                                        {canWrite && (
                                            <td className="px-5 py-3">
                                                <div className="flex items-center gap-1.5">
                                                    <button
                                                        type="button"
                                                        onClick={() => setEditing({ row })}
                                                        className="text-gray-400 hover:text-[#346C75] p-1.5 rounded-md hover:bg-gray-100"
                                                        title={`Edit ${config.noun.toLowerCase()}`}
                                                        aria-label={`Edit ${config.noun.toLowerCase()}`}
                                                    >
                                                        <Pencil size={15} />
                                                    </button>
                                                    {canDeleteLegalRecord(task, row) && (
                                                        <button
                                                            type="button"
                                                            onClick={() => setDeleting(row)}
                                                            className="text-gray-400 hover:text-[#B42318] p-1.5 rounded-md hover:bg-[#FEE9EC]"
                                                            title={`Delete ${config.noun.toLowerCase()}`}
                                                            aria-label={`Delete ${config.noun.toLowerCase()}`}
                                                        >
                                                            <Trash2 size={15} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td
                                        colSpan={config.columns.length + (canWrite ? 1 : 0)}
                                        className="px-5 py-10 text-center text-gray-500">
                                        {config.empty}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
};

export default LegalRecordsPanel;
